import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getGameSessionSummary,
  joinCustomQuizByCode,
  type GameSessionSummaryResponse,
} from './api/gameApi';

interface LocalResultsState {
  endedBy?: 'death' | 'final_year';
  gameVersion?: 'short' | 'long';
  isCustomQuiz?: boolean;
  finalYear?: number;
  finalStability?: number;
  finalScore?: number;
  questionsAnswered?: number;
  questionsCorrect?: number;
  accuracyPercent?: number;
  questionsTotal?: number;
}

const GameResults: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const localState = (location.state ?? {}) as LocalResultsState;
  const [summary, setSummary] = useState<GameSessionSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [replayError, setReplayError] = useState<string | null>(null);
  const [isReplayingCustomQuiz, setIsReplayingCustomQuiz] = useState(false);

  useEffect(() => {
    if (!sessionId || sessionId === 'local') return;
    setIsLoading(true);
    setLoadError(null);
    getGameSessionSummary(sessionId)
      .then((data) => setSummary(data))
      .catch((err) => {
        console.error('Failed to load game summary:', err);
        setLoadError('Could not load full backend results. Showing available local stats.');
      })
      .finally(() => setIsLoading(false));
  }, [sessionId]);

  const questionsAnswered = summary?.questions_answered ?? localState.questionsAnswered ?? 0;
  const questionsCorrect = summary?.questions_correct ?? localState.questionsCorrect ?? 0;
  const score = summary?.score ?? localState.finalScore ?? questionsCorrect;
  const questionsTotal = summary?.questions_total ?? localState.questionsTotal ?? questionsAnswered;
  const isCustomQuiz = Boolean(summary?.custom_quiz) || localState.isCustomQuiz === true;
  const customQuizTitle = summary?.custom_quiz?.title?.trim() || null;
  const replayJoinCode = summary?.custom_quiz?.join_code?.trim() || null;
  const accuracyPercent = summary?.accuracy_percent
    ?? localState.accuracyPercent
    ?? (questionsAnswered > 0 ? Math.round((questionsCorrect / questionsAnswered) * 1000) / 10 : 0);
  const finalYear = localState.finalYear;
  const finalStability = summary?.earth_vitality ?? localState.finalStability;
  const gameVersion = summary?.game_version ?? localState.gameVersion ?? 'short';
  const endedBy = localState.endedBy ?? (
    typeof finalStability === 'number' && finalStability <= 0 ? 'death' : 'final_year'
  );
  const endedByText = isCustomQuiz
    ? (endedBy === 'death' ? 'Custom game ended by collapse' : 'Custom game complete')
    : (endedBy === 'death' ? 'Trial ended by collapse' : 'Trial completed in 2030');
  const resultsHeading = isCustomQuiz ? 'Custom Game Results' : 'Earth Trial Results';
  const sdgText = useMemo(() => {
    if (!summary?.sdgs_surfaced?.length) return 'Not available';
    return summary.sdgs_surfaced.map((sdg) => `SDG ${sdg}`).join(', ');
  }, [summary]);

  const handleReplayCustomQuiz = async () => {
    if (!replayJoinCode || isReplayingCustomQuiz) {
      return;
    }

    setReplayError(null);
    setIsReplayingCustomQuiz(true);

    try {
      const joined = await joinCustomQuizByCode(replayJoinCode);
      sessionStorage.setItem('game:customQuizJoinCode', replayJoinCode);
      const query = new URLSearchParams({
        mode: 'short',
        session_id: joined.session_id,
        custom: '1',
      }).toString();
      navigate(`/game/opening?${query}`);
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : 'Could not start this custom game again right now.';
      setReplayError(message);
      setIsReplayingCustomQuiz(false);
    }
  };

  const handleBackToMenu = () => {
    if (isCustomQuiz) {
      const code = replayJoinCode ?? sessionStorage.getItem('game:customQuizJoinCode');
      if (code) {
        navigate(`/game?customLobby=${encodeURIComponent(code)}`);
        return;
      }
    }
    navigate('/game');
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center px-4 py-8"
      style={{ backgroundImage: "url('/backgrounds/winning-1.png')" }}
    >
      <div className="w-full max-w-4xl rounded-[28px] border border-white/10 bg-[#1b2a41]/95 shadow-2xl p-6 sm:p-8">
        <h1
          className="text-[#FFE896] text-center"
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontWeight: 400,
            fontSize: 'clamp(30px, 4.8vw, 52px)',
            lineHeight: 1.2,
          }}
        >
          {resultsHeading}
        </h1>

        <p className="text-center text-white/70 text-sm sm:text-base mt-2 font-caudex">
          {endedByText}
        </p>

        {isCustomQuiz && customQuizTitle && (
          <p className="text-center text-[#FFE896] text-sm sm:text-base mt-2 font-caudex">
            {customQuizTitle}
          </p>
        )}

        {isLoading && (
          <p className="text-center text-white/70 text-sm mt-4 font-caudex">Loading backend results...</p>
        )}
        {loadError && (
          <p className="text-center text-amber-200 text-sm mt-4 font-caudex">{loadError}</p>
        )}
        {replayError && (
          <p className="text-center text-amber-200 text-sm mt-4 font-caudex">{replayError}</p>
        )}

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <ResultCard label="Final Score" value={`${score}`} />
          <ResultCard label="Questions Correct" value={`${questionsCorrect}/${questionsTotal}`} />
          <ResultCard label="Accuracy" value={`${accuracyPercent}%`} />
          {!isCustomQuiz && (
            <ResultCard label="Final Year Reached" value={finalYear ? `${2020 + finalYear - 1}` : 'Not available'} />
          )}
          <ResultCard label="Final Earth Vitality" value={typeof finalStability === 'number' ? `${finalStability}%` : 'Not available'} />
        </div>

        <div className="mt-4 rounded-xl bg-[#27315D] border border-[#2E3A6B] p-4 text-sm text-white/85 font-caudex">
          <span className="text-white font-semibold font-caudex">SDGs surfaced: </span>
          {sdgText}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
          <button
            onClick={handleBackToMenu}
            className="px-6 py-2.5 rounded-full bg-teal-500 hover:bg-teal-600 transition font-semibold text-sm text-white font-caudex shadow-lg"
          >
            Back to Menu
          </button>
          {isCustomQuiz ? (
            <button
              onClick={handleReplayCustomQuiz}
              disabled={isReplayingCustomQuiz || !replayJoinCode}
              className="px-5 py-2 rounded-full border border-cyan-200/60 hover:bg-white/10 transition font-semibold text-sm text-white font-caudex disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isReplayingCustomQuiz ? 'Starting Custom Game...' : 'Play Custom Game Again'}
            </button>
          ) : (
            <button
              onClick={() => navigate(`/game/character-select?mode=${gameVersion}`)}
              className="px-5 py-2 rounded-full border border-cyan-200/60 hover:bg-white/10 transition font-semibold text-sm text-white font-caudex"
            >
              {gameVersion === 'long' ? 'Play Long Game Again' : 'Play Quick Game Again'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface ResultCardProps {
  label: string;
  value: string;
}

const ResultCard: React.FC<ResultCardProps> = ({ label, value }) => (
  <div className="rounded-xl bg-[#27315D] border border-[#2E3A6B] px-4 py-3 font-caudex">
    <p className="text-xs text-white/60 uppercase tracking-wide font-caudex">{label}</p>
    <p className="text-xl text-white font-semibold mt-1 font-caudex">{value}</p>
  </div>
);

export default GameResults;
